import ClearIcon from '@mui/icons-material/Clear'
import Box from '@mui/material/Box'
import DialogContent from '@mui/material/DialogContent'
import Drawer from '@mui/material/Drawer'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import Image from 'next/image'
import { memo, useMemo } from 'react'

import useTrans from '@/hooks/useTrans'
import { CustomTokenInfo } from '@/utils/helpers'

export const METHOD_TYPE = {
  network: 'network',
  bank: 'bank',
  swapOthers: 'swapOthers',
}

type Props = {
  onClose: () => void
  onClick: (type: string) => void
  selectedDepositToken: CustomTokenInfo | null
  selectedWithdrawToken: CustomTokenInfo | null
  isP2pSupported: (tokenSymbol: string) => boolean
}

const classesDialog = { root: 'outline-none p-0 h-full' }
const MethodSelectionDialog = ({
  onClose,
  onClick,
  selectedDepositToken,
  selectedWithdrawToken,
  isP2pSupported,
}: Props) => {
  const trans = useTrans()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))
  const isOpenDialog = Boolean(selectedDepositToken || selectedWithdrawToken)
  const tokenSymbol = selectedDepositToken?.symbol || selectedWithdrawToken?.symbol
  const tokenDisplaySymbol = selectedDepositToken?.displaySymbol || selectedWithdrawToken?.displaySymbol

  const methodOptions = useMemo(
    () => {
      if(!tokenSymbol) return []
      const isSupportMethod = isP2pSupported(tokenSymbol)
      return selectedWithdrawToken
        ? [
            {
              title: isSupportMethod ? trans.core_tokens_list.bank.withdraw : trans.core_tokens_list.others.withdraw,
              description: isSupportMethod ? trans.core_tokens_list.bank.description : trans.core_tokens_list.others.description(tokenSymbol, true),
              type: METHOD_TYPE.bank,
              dataTestID: 'method-selection-bank',
              icon: '/assets-to-bank-icon.png',
            },
            {
              title: trans.core_tokens_list.network.withdraw,
              description: trans.core_tokens_list.network.description,
              type: METHOD_TYPE.network,
              dataTestID: 'method-selection-network',
              icon: '/assets-to-others-icon.png',
            },
          ]
        : [
            {
              title: isSupportMethod ? trans.core_tokens_list.bank.deposit : trans.core_tokens_list.others.deposit(tokenSymbol),
              description: isSupportMethod ? trans.core_tokens_list.bank.description : trans.core_tokens_list.others.description(tokenSymbol, false),
              type: METHOD_TYPE.bank,
              dataTestID: 'method-selection-bank',
              icon: isSupportMethod ? '/assets-from-bank-icon.png' : '/assets-swap-icon.png',
            },
            {
              title: trans.core_tokens_list.network.deposit,
              description: trans.core_tokens_list.network.description,
              type: METHOD_TYPE.network,
              dataTestID: 'method-selection-network',
              icon: '/assets-from-others-icon.png',
            },
          ]
    }, [tokenSymbol, selectedWithdrawToken, trans, isP2pSupported])

  const renderedContent = (
    <Stack className="bg-[#1E2735] text-white rounded-[16px]">
      <Box
        display="flex"
        justifyContent="space-between"
        className="px-6 py-[11px] bg-[#1A2330] rounded-t-[16px]"
      >
        <Typography className="text-base font-bold">
          {selectedDepositToken ? trans.common.deposit : trans.common.withdraw} {tokenDisplaySymbol}
        </Typography>
        <ClearIcon className="cursor-pointer" onClick={onClose} />
      </Box>
      <Stack className="px-4 py-6" gap="8px">
        {methodOptions.map((method) => (
          <Stack
            key={method.type}
            gap="8px"
            direction="row"
            alignItems="center"
            className="bg-[#141A23] py-5 px-4 rounded-lg cursor-pointer"
            onClick={() => onClick(method.type)}
            data-testid={method.dataTestID}
          >
            <Image
              alt={method.title}
              src={method.icon}
              className="object-cover"
              width={40}
              height={40}
            />
            <Box className="flex-1">
              <Typography className="text-base font-semibold">{method.title}</Typography>
              <Typography className="text-[#9CA3AE] text-sm">{method.description}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )

  if (isDesktop) {
    return (
      <Modal open={isOpenDialog} onClose={onClose}>
        <DialogContent classes={classesDialog}>
          <Box className="w-[343px] sm:w-[484px] max-h-[90vh] overflow-auto absolute top-1/2	left-1/2 translate-x-[-50%] translate-y-[-50%]">
            {renderedContent}
          </Box>
        </DialogContent>
      </Modal>
    )
  }

  return (
    <Drawer
      open={isOpenDialog}
      onClose={onClose}
      anchor="bottom"
      sx={{
        '.MuiPaper-elevation': {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      }}
    >
      {renderedContent}
    </Drawer>
  )
}

export default memo(MethodSelectionDialog)
